import { useEffect, useState } from 'react';
import type { SourcesResponse } from '@swg/contracts';
import { CONNECTOR_RUN_STATUS_LABELS } from '@swg/domain';
import { fetchSources } from '../api.js';
import { theme } from '../theme.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

function statusColor(status: string): string {
  if (status === 'SUCCESS') return theme.color.ok;
  if (status === 'PARTIAL') return '#8A5A00';
  if (status === 'FAILED') return '#B4232B';
  return theme.color.muted;
}

const TERMINAL = new Set(['SUCCESS', 'PARTIAL', 'FAILED', 'SKIPPED', 'CANCELLED']);

/**
 * Stap "Gegevens verzamelen" (conform prototype): toont per bronaanroep de
 * voortgang en status. Blijft pollen zolang de verzameling loopt. Een mislukte
 * bron laat de analyse doorgaan; ontbrekende waarden worden "niet beschikbaar".
 */
export function CollectionProgress({
  runId,
  active,
  onOpenReport,
}: {
  runId: string;
  active: boolean;
  onOpenReport: () => void;
}) {
  const [sources, setSources] = useState<SourcesResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const res = await fetchSources(runId);
        if (cancelled) return;
        setSources(res);
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
      if (!cancelled && active) timer = setTimeout(tick, 3000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, active]);

  const rows = sources?.sources ?? [];
  const doneCount = rows.filter((s) => TERMINAL.has(s.status)).length;
  const okCount = rows.filter((s) => s.status === 'SUCCESS' || s.status === 'PARTIAL').length;
  const total = rows.length;
  const pct = active
    ? total > 0
      ? Math.min(95, Math.round((doneCount / total) * 100))
      : 8
    : 100;

  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: 23, fontWeight: 700, letterSpacing: '-0.01em' }}>
        {active ? 'Gegevens worden verzameld' : 'Verzamelde gegevens'}
      </h1>
      <p style={{ margin: '0 0 22px', color: theme.color.muted, fontSize: 14 }}>
        Eigen bedrijf + concurrenten · een fout bij één bron laat de analyse niet mislukken.
      </p>

      <div
        style={{
          background: theme.color.border,
          borderRadius: 6,
          height: 8,
          overflow: 'hidden',
          marginBottom: 22,
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: theme.color.primary,
            borderRadius: 6,
            transition: 'width 0.5s ease',
          }}
        />
      </div>

      {!loaded && <p style={{ color: theme.color.muted, fontSize: 13.5 }}>Laden…</p>}

      {loaded && total === 0 && (
        <p style={{ color: theme.color.muted, fontSize: 13.5 }}>
          {active
            ? 'De eerste bronaanroepen worden voorbereid…'
            : 'Er zijn geen bronaanroepen geregistreerd voor deze uitvoering.'}
        </p>
      )}

      {total > 0 && (
        <div
          style={{
            background: theme.color.surface,
            border: `1px solid ${theme.color.border}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {rows.map((s) => {
            const failed = s.status === 'FAILED';
            return (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '13px 18px',
                  borderBottom: `1px solid ${theme.color.bg}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {s.connectorName}{' '}
                    <span style={{ color: theme.color.subtle, fontWeight: 400 }}>· {s.subjectName}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: theme.color.muted }}>
                    {formatDate(s.retrievedAt ?? s.finishedAt)}
                    {s.servedFromCache && ' · ♻ uit cache'}
                  </div>
                  {failed && (
                    <div style={{ fontSize: 12, color: '#A33B2E', marginTop: 4 }}>
                      Bron niet bereikbaar — de analyse gaat verder zonder deze gegevens. Ontbrekende
                      waarden worden als “niet beschikbaar” gemarkeerd.
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: statusColor(s.status), whiteSpace: 'nowrap' }}>
                  {CONNECTOR_RUN_STATUS_LABELS[s.status] ?? s.status}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!active && (
        <div
          style={{
            marginTop: 20,
            background: '#E4F3EB',
            border: '1px solid #BCE0CC',
            borderRadius: 10,
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: 13.5, color: '#0F5C37' }}>
            <strong>Verzameling afgerond.</strong>{' '}
            {total > 0
              ? `${okCount} van ${total} bronaanroepen met resultaat.`
              : 'De gegevens zijn verwerkt.'}
          </div>
          <button
            type="button"
            onClick={onOpenReport}
            style={{
              background: theme.color.cta,
              color: theme.color.ctaText,
              border: 'none',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            Bekijk rapport →
          </button>
        </div>
      )}
    </div>
  );
}
